import React, { type KeyboardEvent,useCallback, useEffect, useState } from 'react';
import { useAccessToken, useSubjectLanguage } from '@features/user';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Tooltip,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { MuiTelInput } from 'mui-tel-input';
import { useSnackbar } from 'notistack';

import APIService from '../../services/APIService';
import { type Patient } from '../../types/types';
import { capitalize } from '../../utils/utils';

import {patientValidationSchema } from './patientValidation';

interface NewPatientModalContentProps {
  onClose: () => void;
  onSuccessfulSubmission: (patientId: string) => void;
  simplifiedView?: boolean;
  initialName?: string;
  source?: string;
  isOpen?: boolean; // For tracking purposes
}

export const NewPatientModalContent: React.FC<NewPatientModalContentProps> = ({
  onClose,
  onSuccessfulSubmission,
  simplifiedView = false,
  initialName,
  source = 'unknown',
  isOpen = true,
}) => {
  const { getAccessToken } = useAccessToken();
  const subjectLanguage = useSubjectLanguage();
  // const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm')); // Currently unused
  const { patient: patientMetrics } = useEventTracking();
  const [hasInteractedWithForm, setHasInteractedWithForm] = useState(false);

  const getInitialPatientState = useCallback(() => {
    let firstName = '';
    let lastName = '';

    if (initialName) {
      const nameParts = initialName.trim().split(/\s+/);
      firstName = (nameParts[0] || '').charAt(0).toUpperCase() + (nameParts[0] || '').slice(1);
      lastName =
        nameParts.slice(1).join(' ').charAt(0).toUpperCase() +
          nameParts.slice(1).join(' ').slice(1) || '';
    }

    return {
      first_name: firstName,
      last_name: lastName,
      identifier: '',
      dob: '',
      gender: '',
      pronouns: '',
      ...(simplifiedView
        ? {}
        : {
            middle_name: '',
            phone: '',
            email: '',
            race: '',
            address: '',
          }),
    };
  }, [initialName, simplifiedView]);

  const [patient, setPatient] =
    useState<Omit<Patient, 'patient_id' | 'updated_at'>>(getInitialPatientState());

  // const [isLoading, setIsLoading] = useState(false); // Currently unused
  const [isLoadingIdentifier, setIsLoadingIdentifier] = useState(false);

  // Reset form when modal opens or initialName changes
  useEffect(() => {
    setPatient(getInitialPatientState());

    // Fetch next available identifier when modal opens
    if (isOpen) {
      fetchNextIdentifier();
      // Track modal opening
      patientMetrics.creationStarted(source, {
        simplified_view: simplifiedView,
      });
      // Reset interaction flag when modal opens
      setHasInteractedWithForm(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialName, getInitialPatientState]);

  const fetchNextIdentifier = async () => {
    try {
      setIsLoadingIdentifier(true);
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIGetRequest({
        requestString: '/patient/getNextIdentifier',
        accessToken,
      });

      if (response.ok) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const data = await response.value;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        setPatient(prev => ({ ...prev, identifier: (data).identifier }));
      } else {
        console.error('Failed to get next identifier:', response.error);
      }
    } catch (error) {
      console.error('Error fetching next identifier:', error);
    } finally {
      setIsLoadingIdentifier(false);
    }
  };

  const [_errors, setErrors] = useState({
    name: false,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPatient(prev => ({ ...prev, [name]: value }));
    
    // Mark that user has interacted with form
    if (!hasInteractedWithForm) {
      setHasInteractedWithForm(true);
    }

    // Clear any existing field errors when user types
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'first_name' || name === 'last_name') {
      setErrors(prev => ({ ...prev, name: false }));
    }
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setPatient(prev => ({ ...prev, [name]: value }));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePhoneChange = (newValue: string, _info?: any) => {
    // Simply update the value without any special handling
    setPatient(prev => ({ ...prev, phone: newValue }));

    // Clear error if field has existing error
    if (fieldErrors.phone) {
      setFieldErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const isNameValid = useCallback(() => {
    return (patient.first_name?.length ?? 0) >= 2 || (patient.last_name?.length ?? 0) >= 2;
  }, [patient.first_name, patient.last_name]);

  const handleSubmit = async () => {
    // Create a patient object with a dummy patient_id for validation
    const patientForValidation = { ...patient, patient_id: 'temp' };

    // Validate all fields using Zod schema
    const validationResult = patientValidationSchema.safeParse(patientForValidation);

    if (!validationResult.success) {
      // Extract field errors from Zod validation
      const errors: Record<string, string> = {};
      validationResult.error.errors.forEach(err => {
        const fieldName = err.path[0] as string;
        if (fieldName !== 'patient_id') {
          // Ignore patient_id errors
          errors[fieldName] = err.message;
        }
      });

      setFieldErrors(errors);

      // Show first error message
      const firstError = validationResult.error.errors.find(err => err.path[0] !== 'patient_id');
      if (firstError) {
        enqueueSnackbar(firstError.message, { variant: 'error' });
      }
      return;
    }

    // Clear any existing errors
    setFieldErrors({});
    setIsSubmitting(true);

    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: '/patient/create',
        accessToken,
        body: { patient_data: patient },
      });

      if (response.ok) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const data = await response.value;
        
        // Check if any optional fields were filled
        const hasOptionalFields = !!(
          patient.middle_name ||
          patient.phone ||
          patient.email ||
          patient.race ||
          patient.address ||
          patient.pronouns ||
          patient.gender ||
          patient.dob
        );
        
        // Track successful patient creation
        patientMetrics.created(source, {
          has_optional_fields: hasOptionalFields,
        });
        
        enqueueSnackbar(`${capitalize(subjectLanguage)} created successfully`, {
          variant: 'success',
        });
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        onSuccessfulSubmission((data).patientId);
        handleClose(true); // Pass true to indicate successful creation
      } else {
        // Check if error is an identifier uniqueness collision
        if (response.error_code === 'identifier_uniqueness_collision') {
          enqueueSnackbar(`Identifier already in use. Generating a new one...`, {
            variant: 'warning',
          });
          // Fetch a new identifier
          await fetchNextIdentifier();
          setIsSubmitting(false);
          return;
        }
        throw new Error(response.error?.message || `Failed to create ${subjectLanguage}`);
      }
    } catch (error) {
      console.error('Error creating patient:', error);
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : `Failed to create ${subjectLanguage}. Please try again.`,
        {
          variant: 'error',
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (wasSuccessful = false) => {
    // Track abandonment if user had started filling the form but didn't complete
    if (!wasSuccessful && hasInteractedWithForm && isOpen) {
      patientMetrics.creationAbandoned(source, {
        had_data: true,
      });
    }
    
    setPatient({
      first_name: '',
      last_name: '',
      identifier: '',
      dob: '',
      gender: '',
      pronouns: '',
      ...(simplifiedView
        ? {}
        : {
            middle_name: '',
            phone: '',
            email: '',
            race: '',
            address: '',
          }),
    });
    setErrors({ name: false });
    setFieldErrors({});
    setHasInteractedWithForm(false);
    onClose();
  };

  // Handle Enter key press to submit form
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.ctrlKey &&
      isNameValid() &&
      !isSubmitting
    ) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <>
      <DialogTitle>
        New {capitalize(subjectLanguage)}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} onKeyDown={handleKeyDown}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="First Name"
                name="first_name"
                value={patient.first_name}
                onChange={handleChange}
                error={!!fieldErrors.first_name}
                helperText={fieldErrors.first_name}
                fullWidth
                autoFocus
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Last Name"
                name="last_name"
                value={patient.last_name}
                onChange={handleChange}
                error={!!fieldErrors.last_name}
                helperText={fieldErrors.last_name}
                fullWidth
              />
            </Grid>
          </Grid>

          <Divider>
            <Chip label="Optional Fields" size="small" />
          </Divider>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Identifier (PRN)"
                name="identifier"
                value={patient.identifier}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  readOnly: isLoadingIdentifier,
                  endAdornment: isLoadingIdentifier ? (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Date of Birth"
                name="dob"
                type="date"
                value={patient.dob}
                onChange={handleChange}
                error={!!fieldErrors.dob}
                helperText={fieldErrors.dob}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().split('T')[0],
                }}
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="gender-label">Gender</InputLabel>
                <Select
                  labelId="gender-label"
                  name="gender"
                  value={patient.gender || ''}
                  onChange={handleSelectChange}
                  label="Gender"
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="non-binary">Non-binary</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                  <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Pronouns"
                name="pronouns"
                value={patient.pronouns}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box>
                <MuiTelInput
                  label="Phone"
                  value={patient.phone}
                  onChange={(newValue, info) => handlePhoneChange(newValue, info)}
                  defaultCountry="US"
                  forceCallingCode
                  onlyCountries={['US', 'CA', 'GB', 'AU', 'NZ', 'ZA', 'MX']}
                  fullWidth
                  error={!!fieldErrors.phone}
                  sx={{
                    '& input': {
                      height: '1.4375em',
                    },
                  }}
                />
                {fieldErrors.phone && (
                  <FormHelperText error sx={{ mx: 1.75 }}>
                    {fieldErrors.phone}
                  </FormHelperText>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={patient.email}
                onChange={handleChange}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
                fullWidth
              />
            </Grid>
          </Grid>

          {!simplifiedView && (
            <>
              <TextField
                label="Middle Name"
                name="middle_name"
                value={patient.middle_name}
                onChange={handleChange}
                error={!!fieldErrors.middle_name}
                helperText={fieldErrors.middle_name}
                fullWidth
              />
              <TextField
                label="Race"
                name="race"
                value={patient.race}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                label="Address"
                name="address"
                value={patient.address}
                onChange={handleChange}
                multiline
                rows={2}
                fullWidth
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose()} disabled={isSubmitting}>
          Cancel
        </Button>
        <Tooltip
          title={
            !isNameValid()
              ? 'At least one name field must have at least two characters'
              : isNameValid() && !isSubmitting
                ? 'Press Enter to create'
                : ''
          }
          placement="top"
        >
          <span>
            <Box sx={{ position: 'relative' }}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                disabled={isSubmitting || !isNameValid()}
                sx={{
                  paddingRight: isNameValid() && !isSubmitting ? '30px' : '16px',
                }}
              >
                {isSubmitting ? 'Creating...' : `Create ${capitalize(subjectLanguage)}`}
              </Button>
              {isNameValid() && !isSubmitting && (
                <KeyboardReturnIcon
                  fontSize="small"
                  sx={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '1rem',
                    opacity: 0.7,
                    color: 'white',
                  }}
                />
              )}
            </Box>
          </span>
        </Tooltip>
      </DialogActions>
    </>
  );
};

export default NewPatientModalContent;